import { For } from '../c-mp/comp/For'
import { Icon } from '../c-mp/comp/Icon'
import { $when, Show } from '../c-mp/comp/Show'
import { Slot } from '../c-mp/comp/Slot'
import { defineComponent } from '../c-mp/fun/defineComponent'
import { unproxify } from '../c-mp/fun/proxify'
import { url } from '../c-mp/fun/url'
import { useMemo } from '../c-mp/fun/useMemo'
import { formatDate } from '../fun/formatDate'
import { formatNumber } from '../fun/formatNumber'
import { getDataOrThrow } from '../fun/getDataOrThrow'
import { getPersonAliasesFormatted } from '../fun/getPersonAliasesFormatted'
import { getPersonNameFormatted } from '../fun/getPersonNameFormatted'
import { isDead } from '../fun/isDead'
import { setPageTitle } from '../fun/setPageTitle'
import { onPopState } from '../fun/useRouting'
import { ICON_FIRST } from '../icon/ICON_FIRST'
import { ICON_LAST } from '../icon/ICON_LAST'
import { ICON_LEFT } from '../icon/ICON_LEFT'
import { ICON_RIGHT } from '../icon/ICON_RIGHT'
import { appState } from '../model/appState'
import { _ } from '../model/ccc'
import { PortraitComp } from './PortraitComp'

export const PageSearchComp = defineComponent<{}>(
	'PageSearchComp',
	(props, $) => {
		setPageTitle('Kereső')

		const data = getDataOrThrow()

		let inputElem: HTMLInputElement

		function getQueryParamsFromUrl() {
			return new URLSearchParams(appState.pathMatch?.search.input)
		}
		function setQueryParamsToUrl(params: URLSearchParams) {
			history.replaceState(undefined, '', url`/search?` + params.toString())
			onPopState()
		}
		const getQueryFromUrl = useMemo('getQueryFromUrl', () => {
			return getQueryParamsFromUrl().get('query') ?? ''
		})
		function setQueryToUrl(query: string) {
			const params = getQueryParamsFromUrl()
			if (params.get('query') === query) return
			params.set('query', query)
			setQueryParamsToUrl(params)
		}
		const getPageFromUrl = useMemo('getPageFromUrl', () => {
			let result = parseInt(getQueryParamsFromUrl().get('page') ?? '1', 10)
			if (isNaN(result) || !isFinite(result) || result < 1) {
				result = 1
			}
			if (result > getPageCount()) {
				result = getPageCount()
			}
			return result
		})
		function setPageToUrl(page: number) {
			const params = getQueryParamsFromUrl()
			if (params.get('page') === page.toString()) return
			params.set('page', page.toString())
			setQueryParamsToUrl(params)
		}

		const getTextAndPerson = useMemo('getTextAndPerson', () =>
			Object.values(data.personsById)
				.map((person) => ({
					text: [
						person.honorific,
						person.lastName,
						person.firstName,
						person.aliases
							?.map((it) => [it.honorific, it.lastName, it.firstName].join(' '))
							.join(' '),
						'(' + (person.nickname ?? '') + ')',
						isDead(data, person) ? '†+' : ':)',
						':',
						person.info,
						formatDate(person.birthDate),
						person.birthDate,
						'(' + (person.birthPlace ?? '') + ')',
						'–',
						formatDate(person.deathDate),
						person.deathDate,
						'(' + (person.deathPlace ?? '') + ')',
						'{' + person.cloudId + '}',
					]
						.join(' ')
						.toLocaleLowerCase('hu'),
					person,
				}))
				.toSorted((a, b) =>
					(b.person.birthDate ?? '').localeCompare(a.person.birthDate ?? ''),
				),
		)

		const getSanitizedQuery = useMemo('getSanitizedQuery', () =>
			getQueryFromUrl()
				.trim()
				.split(/\s+/g)
				.map((it) => it.toLocaleLowerCase('hu')),
		)

		const ITEMS_PER_PAGE = 25

		const getResults = useMemo('getResults', () => {
			const query = unproxify(getSanitizedQuery())
			return getTextAndPerson()
				.filter((it) => query.find((word) => !it.text.includes(word)) == null)
				.map((it) => it.person)
		})

		const getPageCount = useMemo(
			'getPageCount',
			() => Math.ceil(getResults().length / ITEMS_PER_PAGE) || 1,
		)

		// useEffect('input focus [ta70tv]', () => {
		// 	inputElem.focus()
		// })

		let inputTimeoutRef: ReturnType<typeof setTimeout> | undefined

		return (
			<div class={_.ccc_page}>
				<h1>Kereső</h1>
				<input
					ref={(it) => (inputElem = it)}
					id='tceq7c'
					value={getQueryFromUrl}
					oninput={() => {
						const query = inputElem.value
						const page = 1
						clearTimeout(inputTimeoutRef)
						inputTimeoutRef = setTimeout(() => {
							setQueryToUrl(query)
							setPageToUrl(page)
						}, 500)
					}}
					placeholder='Keresés...'
				/>
				<p>
					Találatok: <Slot get={() => formatNumber(getResults().length)} />
				</p>
				<div class={_.ccc_tiles__centered}>
					<For
						debugName='Találatok'
						each={() =>
							getResults().slice(
								(getPageFromUrl() - 1) * ITEMS_PER_PAGE,
								getPageFromUrl() * ITEMS_PER_PAGE,
							)
						}
						render={({ get }) => (
							<a
								class={_.ccc_button__person}
								href={() => url`/person/${get().id}`}
							>
								<PortraitComp getPerson={get} />
								<div>
									<Slot get={() => getPersonNameFormatted(data, get())} />
									<Show
										it={$when(
											() => get().aliases,
											({ get }) => (
												<div>
													<small>Más néven:</small>{' '}
													<Slot get={() => getPersonAliasesFormatted(get())} />
												</div>
											),
										)}
									/>
									<Slot
										get={() =>
											get().info ? (
												<div>
													<small>{get().info}</small>
												</div>
											) : (
												''
											)
										}
									/>{' '}
									<div class={_.ccc_date}>
										<Slot get={() => formatDate(get().birthDate)} />
										<Slot
											get={() =>
												get().birthPlace ? ` (${get().birthPlace})` : ''
											}
										/>{' '}
										– <Slot get={() => formatDate(get().deathDate)} />
										<Slot
											get={() =>
												get().deathPlace ? ` (${get().deathPlace})` : ''
											}
										/>
									</div>
								</div>
							</a>
						)}
					/>
				</div>
				<div class={_.ccc_tiles__centered__compact}>
					<button
						class={_.ccc_button__rounded}
						onclick={() => {
							setPageToUrl(1)
							scrollTo({ top: 0, left: 0 })
						}}
						disabled={() => getPageFromUrl() === 1}
					>
						<Icon svg={ICON_FIRST} /> Első oldal
					</button>
					<button
						class={_.ccc_button__rounded}
						onclick={() => {
							setPageToUrl(getPageFromUrl() - 1)
							scrollTo({ top: 0, left: 0 })
						}}
						disabled={() => getPageFromUrl() === 1}
					>
						<Icon svg={ICON_LEFT} /> Előző oldal
					</button>
					<div class={_.ccc_button_padding}>
						<Slot get={() => `${getPageFromUrl()} / ${getPageCount()}`} />
					</div>
					<button
						class={_.ccc_button__rounded}
						onclick={() => {
							setPageToUrl(getPageFromUrl() + 1)
							scrollTo({ top: 0, left: 0 })
						}}
						disabled={() => getPageFromUrl() >= getPageCount()}
					>
						<Icon svg={ICON_RIGHT} /> Következő oldal
					</button>
					<button
						class={_.ccc_button__rounded}
						onclick={() => {
							setPageToUrl(getPageCount())
							scrollTo({ top: 0, left: 0 })
						}}
						disabled={() => getPageFromUrl() >= getPageCount()}
					>
						<Icon svg={ICON_LAST} /> Utolsó oldal
					</button>
				</div>
			</div>
		)
	},
)
